import React, { useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PackageWithRelations } from '@/features/packages/types';
import { Edit2, Trash2, ChevronUp, ChevronDown } from 'lucide-react';

interface PackageTableProps {
  packages: PackageWithRelations[];
  isLoading?: boolean;
  onEdit: (pkg: PackageWithRelations) => void;
  onDelete: (packageId: number) => void;
}

export const PackageTable: React.FC<PackageTableProps> = ({
  packages,
  isLoading = false,
  onEdit,
  onDelete,
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns: ColumnDef<PackageWithRelations>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <div className="flex items-center gap-2">
          Name
          <button
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="ml-2"
          >
            {column.getIsSorted() === 'asc' && <ChevronUp className="h-4 w-4" />}
            {column.getIsSorted() === 'desc' && <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      ),
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('name')}</div>
      ),
    },
    {
      accessorKey: 'package_type',
      header: 'Type',
      cell: ({ row }) => {
        const type = row.getValue('package_type') as string;
        const typeColors: Record<string, string> = {
          standalone: 'bg-blue-100 text-blue-800',
          plan_bundle: 'bg-purple-100 text-purple-800',
          category_bundle: 'bg-green-100 text-green-800',
          mixed: 'bg-orange-100 text-orange-800',
        };
        return (
          <Badge variant="outline" className={typeColors[type] || ''}>
            {type.replace(/_/g, ' ')}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'monthly_price',
      header: 'Monthly Price',
      cell: ({ row }) => {
        const price = row.getValue('monthly_price') as number | null;
        return price ? `$${price.toFixed(2)}` : '-';
      },
    },
    {
      accessorKey: 'yearly_price',
      header: 'Yearly Price',
      cell: ({ row }) => {
        const price = row.getValue('yearly_price') as number | null;
        return price ? `$${price.toFixed(2)}` : '-';
      },
    },
    {
      accessorKey: 'channel_count',
      header: 'Channels',
      cell: ({ row }) => {
        const count = row.original.channel_count;
        return (
          <Badge variant="secondary">
            {count} channel{count !== 1 ? 's' : ''}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'plan_count',
      header: 'Plans',
      cell: ({ row }) => {
        const count = row.original.plan_count;
        return count > 0 ? count : '-';
      },
    },
    {
      accessorKey: 'category_count',
      header: 'Categories',
      cell: ({ row }) => {
        const count = row.original.category_count;
        return count > 0 ? count : '-';
      },
    },
    {
      accessorKey: 'is_featured',
      header: 'Featured',
      cell: ({ row }) => {
        const isFeatured = row.getValue('is_featured') as boolean;
        return isFeatured ? (
          <Badge variant="default" className="bg-green-600">
            Featured
          </Badge>
        ) : (
          '-'
        );
      },
    },
    {
      accessorKey: 'is_addon',
      header: 'Add-on',
      cell: ({ row }) => {
        const isAddon = row.getValue('is_addon') as boolean;
        return isAddon ? (
          <Badge variant="secondary">Add-on</Badge>
        ) : (
          '-'
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(row.original)}
            className="h-8 w-8 p-0"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm(`Delete package "${row.original.name}"?`)) {
                onDelete(row.original.id);
              }
            }}
            className="h-8 w-8 p-0 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: packages,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
        <p className="text-gray-500">No packages found. Create one to get started!</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="bg-gray-50">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} className="hover:bg-gray-50">
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default PackageTable;
